import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { listBotVersions, listLiveRuns, stopLiveRun } from '../../api/botsApi';
import { fetchAccounts } from '../../api/api';
import PageLayout from '../../components/PageLayout';
import { Button } from '../../components/ui/button';
import StatusBadge from '../../components/bots/StatusBadge';

export default function LiveRunsPage() {
  const { botId } = useParams();
  const [runs, setRuns] = useState([]);
  const [versions, setVersions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [busyMap, setBusyMap] = useState({});
  const timerRef = useRef(null);

  const accountNameMap = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      map[acc.id] = `${acc.name} (${acc.platform})`;
    });
    return map;
  }, [accounts]);

  const versionNameMap = useMemo(() => {
    const map = {};
    (versions || []).forEach((v) => {
      map[v.id] = v.version_name || v.id?.slice(0, 8);
    });
    return map;
  }, [versions]);

  const fetchAll = useCallback(async () => {
    if (!botId) return;
    setIsRefreshing(true);
    setError(null);
    try {
      const vRes = await listBotVersions(botId);
      const vList = Array.isArray(vRes?.results) ? vRes.results : vRes;
      setVersions(Array.isArray(vList) ? vList : []);
      const runLists = await Promise.all(
        (vList || []).map((v) => listLiveRuns({ bot_version_id: v.id }).catch(() => []))
      );
      const flattened = runLists
        .map((r) => (Array.isArray(r?.results) ? r.results : r))
        .flat()
        .filter(Boolean);
      setRuns(flattened);
    } catch (e) {
      console.error('Failed to load live runs', e);
      setError(e?.message || 'Failed to load live runs');
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  }, [botId]);

  const startPolling = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(fetchAll, 8000);
  }, [fetchAll]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const accs = await fetchAccounts();
        if (!mounted) return;
        setAccounts(Array.isArray(accs?.results) ? accs.results : accs);
      } catch (e) {
        console.warn('Failed to fetch accounts', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    fetchAll();
    startPolling();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchAll, startPolling]);

  const handleStop = async (runId) => {
    setBusyMap((m) => ({ ...m, [runId]: true }));
    try {
      await stopLiveRun(runId);
      await fetchAll();
      startPolling();
    } catch (e) {
      console.error('Failed to stop run', e);
      alert(e?.message || 'Failed to stop run');
    } finally {
      setBusyMap((m) => ({ ...m, [runId]: false }));
    }
  };

  const headerRight = (
    <div className="space-x-2">
      <Button onClick={fetchAll} variant="outline">{isRefreshing ? 'Refreshing…' : 'Refresh'}</Button>
    </div>
  );

  if (isLoading) {
    return (
      <PageLayout
        title="Live Runs"
        headerRightContent={headerRight}
        backButton={{ label: 'Back to Bot', to: `/bots/${botId}`, preferHistory: false, hideLabelOnMobile: true }}
      >
        <div className="p-4">Loading…</div>
      </PageLayout>
    );
  }
  if (error) {
    return (
      <PageLayout
        title="Live Runs"
        headerRightContent={headerRight}
        backButton={{ label: 'Back to Bot', to: `/bots/${botId}`, preferHistory: false, hideLabelOnMobile: true }}
      >
        <div className="p-4 text-destructive">{error}</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Live Runs"
      headerRightContent={headerRight}
      backButton={{ label: 'Back to Bot', to: `/bots/${botId}`, preferHistory: false, hideLabelOnMobile: true }}
    >
      <div className="space-y-4">
        <div className="text-sm text-textSecondary">{isRefreshing ? 'Refreshing…' : ''}</div>
        <div className="overflow-x-auto border border-borderDefault rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-backgroundSubtle">
              <tr>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Version</th>
                <th className="text-left p-2">Instrument</th>
                <th className="text-left p-2">Timeframe</th>
                <th className="text-left p-2">Account</th>
                <th className="text-left p-2">Started</th>
                <th className="text-left p-2">Stopped</th>
                <th className="text-left p-2">Last error</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {runs && runs.length > 0 ? (
                runs.map((r) => {
                  const terminal = ['STOPPED', 'ERROR'].includes(r.status);
                  return (
                    <tr key={r.id} className="border-t border-borderDefault">
                      <td className="p-2"><StatusBadge status={r.status} /></td>
                      <td className="p-2 font-medium">
                        <Link to={`/bots/${botId}/versions/${r.bot_version}`} className="text-primary hover:underline">
                          {versionNameMap[r.bot_version] || r.bot_version?.slice(0, 8)}
                        </Link>
                      </td>
                      <td className="p-2">{r.instrument_symbol}</td>
                      <td className="p-2">{r.timeframe || '-'}</td>
                      <td className="p-2">{accountNameMap[r.account_id] || r.account_id}</td>
                      <td className="p-2">{r.started_at ? new Date(r.started_at).toLocaleString() : '-'}</td>
                      <td className="p-2">{r.stopped_at ? new Date(r.stopped_at).toLocaleString() : '-'}</td>
                      <td className="p-2 max-w-xs">{r.last_error ? <span title={r.last_error} className="truncate inline-block max-w-[220px] align-bottom">{r.last_error}</span> : '-'}</td>
                      <td className="p-2 text-right">
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={terminal || busyMap[r.id]}
                          onClick={() => handleStop(r.id)}
                        >
                          {busyMap[r.id] ? 'Stopping…' : 'Stop'}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="p-4 text-center text-textSecondary">No live runs</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
}
